/**
 * useAppointmentLiveTimers - Real-time Appointment Timer Hook
 * 
 * Subscribes to the operations:client:{clientId} WebSocket channel to receive
 * live SmartTask workflow state transitions (en_route, in_progress, etc.).
 * Maintains running timers for active appointments and provides HH:MM:SS display.
 * 
 * SmartTask is the source of truth — this hook starts a visual timer when a
 * WebSocket signal arrives and keeps it running independently at 1-second ticks.
 * 
 * @created March 2, 2026
 */

'use client';

import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useWebSocketChannel } from '@/hooks/useWebSocketChannel';
import { useAuth } from '@/hooks/useAuth';
import { authService } from '@/lib/authService';
import {
  getOperationsRuntimeAppointmentTimers,
  removeOperationsRuntimeAppointmentTimers,
  upsertOperationsRuntimeAppointmentTimers,
  type OperationsAppointmentRuntimeTimer,
  type AppointmentRuntimeTimerState,
  type OperationsRuntimeSnapshot,
} from '@/lib/operations/operationsRuntimeSnapshot';
import { useOperationsRuntimeSnapshot } from '@/lib/operations/useOperationsRuntimeSnapshot';
import { normalizeOperationsRealtimeMessage } from '@/lib/websocket/operationsRealtime';

// ============================================================================
// TYPES
// ============================================================================

export interface AppointmentTimerState {
  appointmentId: string;
  /** 'en_route' | 'in_progress' */
  activeState: AppointmentRuntimeTimerState;
  /** ISO timestamp when this state started (from SmartTask) */
  startedAt: string;
  /** The appointment's scheduled_start ISO, for late detection */
  scheduledStart?: string;
  /** The appointment's scheduled_end ISO, for over-budget detection */
  scheduledEnd?: string;
  /** User ID of the field worker */
  userId?: number;
  /** User name of the field worker */
  userName?: string;
  /** Accumulated elapsed time from earlier segments */
  elapsedOffsetSeconds?: number;
  /** Frozen paused timer state — preserves elapsed time without ticking */
  isPaused?: boolean;
}

export interface AppointmentTimerDisplay extends AppointmentTimerState {
  /** Elapsed seconds since startedAt */
  elapsedSeconds: number;
  /** Formatted as HH:MM:SS */
  elapsedFormatted: string;
  /** Whether the timer is actively ticking */
  isRunning: boolean;
  /** Is the worker late? (en_route and past scheduledStart) */
  isLate: boolean;
  /** Is the job over schedule? (in_progress and past scheduledEnd) */
  isOverSchedule: boolean;
  /** How many seconds late (past scheduledStart), 0 if on time */
  lateSeconds: number;
}

// Operations channel message types we care about
interface AppointmentStatusEvent {
  type: 'appointment_status_changed';
  appointment_id: string;
  old_status: string;
  new_status: string;
  /** ISO timestamp of the transition */
  timestamp: string;
  user_id?: number;
  user_name?: string;
  scheduled_start?: string;
  scheduled_end?: string;
  /** travel_started_at from custom_fields */
  travel_started_at?: string;
  /** check_in_timestamp from custom_fields */
  check_in_timestamp?: string;
  /** workflow_paused_at from custom_fields */
  workflow_paused_at?: string | null;
  /** workflow_resumed_at from custom_fields */
  workflow_resumed_at?: string | null;
  /** accumulated_work_seconds when present on the payload */
  accumulated_work_seconds?: number | string | null;
  /** Set on workflow idle completion; partial/reschedule/cancel keep DB status unresolved */
  resolution?: string | null;
  resolution_type?: string | null;
  workflow_state?: string | null;
  target_user_id?: number;
  target_user_ids?: number[];
  assignee_states?: AppointmentStatusAssigneeState[];
  check_out_timestamp?: string | null;
}

interface AppointmentStatusAssigneeState {
  user_id: number;
  display_name?: string | null;
  workflow_state: string;
  state_started_at?: string | null;
  active_travel_started_at?: string | null;
  active_work_started_at?: string | null;
  active_travel_elapsed_offset_seconds?: number | string | null;
  active_work_elapsed_offset_seconds?: number | string | null;
  is_travel_running?: boolean;
  is_work_running?: boolean;
}

type AppointmentTimerDirective =
  | { clear: true }
  | { timer: AppointmentTimerState };

const TERMINAL_APPOINTMENT_STATUSES_FOR_TIMERS = [
  'resolved',
  'completed',
  'cancelled',
  'closed',
  'rescheduled',
  'missed',
] as const;

/** Visit completed but work item left unresolved — still must clear every assignee's live timer */
const UNRESOLVED_COMPLETION_RESOLUTIONS = new Set(['partial', 'rescheduled', 'cancelled']);

function shouldClearAppointmentTimerOnStatusEvent(event: AppointmentStatusEvent): boolean {
  const { new_status: newStatus, resolution } = event;
  if (TERMINAL_APPOINTMENT_STATUSES_FOR_TIMERS.includes(newStatus as (typeof TERMINAL_APPOINTMENT_STATUSES_FOR_TIMERS)[number])) {
    return true;
  }
  if (newStatus === 'unresolved' && typeof resolution === 'string' && UNRESOLVED_COMPLETION_RESOLUTIONS.has(resolution)) {
    return true;
  }
  return false;
}

// ============================================================================
// HELPERS
// ============================================================================

function formatElapsed(totalSeconds: number): string {
  const abs = Math.abs(Math.floor(totalSeconds));
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const s = abs % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function getElapsedOffsetAtTimestamp(timer: AppointmentTimerState, referenceTimestamp?: string | null): number {
  const elapsedOffsetSeconds = Math.max(0, timer.elapsedOffsetSeconds || 0);
  if (timer.isPaused) {
    return elapsedOffsetSeconds;
  }

  const startMs = new Date(timer.startedAt).getTime();
  const endMs = referenceTimestamp ? new Date(referenceTimestamp).getTime() : Date.now();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    return elapsedOffsetSeconds;
  }

  return elapsedOffsetSeconds + Math.max(0, Math.floor((endMs - startMs) / 1000));
}

function getNonNegativeFiniteSeconds(value: unknown): number | null {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return null;
  }

  return Math.floor(parsed);
}

function getStoredWorkElapsedSeconds(customFields?: Record<string, unknown> | null): number {
  return getNonNegativeFiniteSeconds(customFields?.accumulated_work_seconds) ?? 0;
}

function getWorkSegmentStartedAt(
  customFields?: Record<string, unknown> | null,
  options?: {
    preferResumeSegment?: boolean;
    fallback?: string | null;
  },
): string {
  const workflowResumedAt = typeof customFields?.workflow_resumed_at === 'string' ? customFields.workflow_resumed_at : '';
  const workStartedAt = typeof customFields?.work_started_at === 'string' ? customFields.work_started_at : '';
  const checkInTimestamp = typeof customFields?.check_in_timestamp === 'string' ? customFields.check_in_timestamp : '';
  const fallback = typeof options?.fallback === 'string' ? options.fallback : '';

  if (options?.preferResumeSegment) {
    return workflowResumedAt || workStartedAt || fallback || checkInTimestamp || new Date().toISOString();
  }

  return workStartedAt || checkInTimestamp || workflowResumedAt || fallback || new Date().toISOString();
}

function serializeTimerMap(timers: Map<string, AppointmentTimerState>): string {
  return JSON.stringify(
    Array.from(timers.entries())
      .sort(([leftId], [rightId]) => leftId.localeCompare(rightId))
      .map(([appointmentId, timer]) => [
        appointmentId,
        timer.activeState,
        timer.startedAt,
        timer.scheduledStart || null,
        timer.scheduledEnd || null,
        timer.userId ?? null,
        timer.userName || null,
        timer.elapsedOffsetSeconds ?? 0,
        timer.isPaused ?? false,
      ])
  );
}

function toRuntimeTimer(timer: AppointmentTimerState, source: OperationsAppointmentRuntimeTimer['source']): OperationsAppointmentRuntimeTimer {
  return {
    appointmentId: timer.appointmentId,
    activeState: timer.activeState,
    startedAt: timer.startedAt,
    scheduledStart: timer.scheduledStart,
    scheduledEnd: timer.scheduledEnd,
    userId: timer.userId,
    userName: timer.userName,
    elapsedOffsetSeconds: timer.elapsedOffsetSeconds ?? 0,
    isPaused: timer.isPaused ?? false,
    updatedAt: new Date().toISOString(),
    source,
  };
}

function fromRuntimeTimer(timer: OperationsAppointmentRuntimeTimer): AppointmentTimerState {
  return {
    appointmentId: timer.appointmentId,
    activeState: timer.activeState,
    startedAt: timer.startedAt,
    scheduledStart: timer.scheduledStart,
    scheduledEnd: timer.scheduledEnd,
    userId: timer.userId,
    userName: timer.userName,
    elapsedOffsetSeconds: timer.elapsedOffsetSeconds ?? 0,
    isPaused: timer.isPaused ?? false,
  };
}

function getCurrentUserAssigneeSnapshot(
  event: AppointmentStatusEvent,
  currentUserId: number,
): AppointmentStatusAssigneeState | null {
  if (!currentUserId || !Array.isArray(event.assignee_states)) {
    return null;
  }

  return event.assignee_states.find((snapshot) => Number(snapshot?.user_id) === Number(currentUserId)) || null;
}

function getTimerDirectiveFromAssigneeSnapshot(options: {
  appointmentId: string;
  event: AppointmentStatusEvent;
  snapshot: AppointmentStatusAssigneeState;
}): AppointmentTimerDirective | null {
  const { appointmentId, event, snapshot } = options;
  const workflowState = String(snapshot.workflow_state || '').trim().toLowerCase();

  if (!workflowState) {
    return null;
  }

  if (workflowState === 'traveling' || workflowState === 'travel_paused') {
    return {
      timer: {
        appointmentId,
        activeState: 'en_route',
        startedAt: snapshot.active_travel_started_at || event.travel_started_at || snapshot.state_started_at || event.timestamp || new Date().toISOString(),
        scheduledStart: event.scheduled_start,
        scheduledEnd: event.scheduled_end,
        userId: snapshot.user_id,
        userName: snapshot.display_name || undefined,
        elapsedOffsetSeconds: getNonNegativeFiniteSeconds(snapshot.active_travel_elapsed_offset_seconds) ?? 0,
        isPaused: workflowState === 'travel_paused',
      },
    };
  }

  if (workflowState === 'checked_in' || workflowState === 'completing' || workflowState === 'paused') {
    return {
      timer: {
        appointmentId,
        activeState: 'in_progress',
        startedAt: snapshot.active_work_started_at
          || (workflowState === 'paused' ? event.workflow_paused_at : event.workflow_resumed_at)
          || event.check_in_timestamp
          || snapshot.state_started_at
          || event.timestamp
          || new Date().toISOString(),
        scheduledStart: event.scheduled_start,
        scheduledEnd: event.scheduled_end,
        userId: snapshot.user_id,
        userName: snapshot.display_name || undefined,
        elapsedOffsetSeconds: getNonNegativeFiniteSeconds(snapshot.active_work_elapsed_offset_seconds) ?? 0,
        isPaused: workflowState === 'paused',
      },
    };
  }

  if (workflowState === 'arrived' || workflowState === 'idle' || workflowState === 'ready') {
    return { clear: true };
  }

  return null;
}

// ============================================================================
// HOOK
// ============================================================================

export function useAppointmentLiveTimers(options?: {
  /** Only track timers for these appointment IDs (optimization) */
  appointmentIds?: string[];
  /** Enable the 1-second ticking clock used for elapsed timer displays. Default: true */
  enableTick?: boolean;
}) {
  const { user, client } = useAuth();
  // Session active client (JWT client_id claim) — user.client_id is 0 for
  // root/multi-client users and would silently disable the subscription.
  const clientId = authService.getSessionClientId() ?? client?.id;
  const userId = user?.id ? Number(user.id) : 0;
  const realtimeTaskChannel = clientId && userId > 0
    ? `tasks:user:${userId}:client:${clientId}`
    : undefined;
  const instanceIdRef = useRef(`appointment-timers-${Math.random().toString(36).slice(2, 10)}`);
  const hydratedSnapshotRef = useRef<string | null>(null);
  const appointmentIdsSignature = useMemo(() => {
    if (!options?.appointmentIds || options.appointmentIds.length === 0) {
      return '';
    }

    return [...options.appointmentIds]
      .map((appointmentId) => String(appointmentId))
      .sort()
      .join('|');
  }, [options?.appointmentIds]);
  const trackedAppointmentIds = useMemo(() => {
    if (!appointmentIdsSignature) {
      return null;
    }

    return new Set(appointmentIdsSignature.split('|'));
  }, [appointmentIdsSignature]);
  const { snapshot } = useOperationsRuntimeSnapshot({
    clientId,
    userId,
    sourceId: instanceIdRef.current,
  });
  
  // Map of appointmentId → timer state
  const [timers, setTimers] = useState<Map<string, AppointmentTimerState>>(new Map());
  const [statusOverrides, setStatusOverrides] = useState<Map<string, string>>(new Map());

  // Ref mirror of statusOverrides set by WebSocket — prevents stale seedFromAppointments
  // data from overwriting real-time status changes (e.g., timer cleared by 'resolved' event
  // but seed data still has 'in_progress').
  const wsStatusRef = useRef<Map<string, string>>(new Map());
  
  // Tick counter for re-renders (increments every second)
  const [tick, setTick] = useState(0);
  const tickRef = useRef<NodeJS.Timeout | null>(null);

  const hydrateFromRuntimeSnapshot = useCallback((snapshot: OperationsRuntimeSnapshot | null | undefined) => {
    if (!snapshot || !clientId || !userId) {
      return;
    }

    const nextTimers = new Map<string, AppointmentTimerState>();
    const runtimeTimers = getOperationsRuntimeAppointmentTimers(
      snapshot,
      trackedAppointmentIds ? Array.from(trackedAppointmentIds) : undefined,
    );
    runtimeTimers.forEach((timer, appointmentId) => {
      nextTimers.set(appointmentId, fromRuntimeTimer(timer));
    });

    const serialized = serializeTimerMap(nextTimers);
    if (hydratedSnapshotRef.current === serialized) {
      return;
    }

    hydratedSnapshotRef.current = serialized;
    setTimers((previous) => serializeTimerMap(previous) === serialized ? previous : nextTimers);
  }, [clientId, trackedAppointmentIds, userId]);

  const persistTimerUpsert = useCallback((nextTimers: AppointmentTimerState[], source: OperationsAppointmentRuntimeTimer['source']) => {
    if (!clientId || !userId || nextTimers.length === 0) {
      return;
    }

    void upsertOperationsRuntimeAppointmentTimers({
      clientId,
      userId,
      timers: nextTimers.map((timer) => toRuntimeTimer(timer, source)),
      source: instanceIdRef.current,
    });
  }, [clientId, userId]);

  const persistTimerRemoval = useCallback((appointmentIds: string[]) => {
    if (!clientId || !userId || appointmentIds.length === 0) {
      return;
    }

    void removeOperationsRuntimeAppointmentTimers({
      clientId,
      userId,
      appointmentIds,
      source: instanceIdRef.current,
    });
  }, [clientId, userId]);

  useEffect(() => {
    if (!snapshot || !clientId || !userId) {
      hydratedSnapshotRef.current = null;
      return;
    }
    hydrateFromRuntimeSnapshot(snapshot);
  }, [clientId, hydrateFromRuntimeSnapshot, snapshot, userId]);
  
  // Track whether we have any active timers
  const hasActiveTimers = timers.size > 0;
  const hasRunningTimers = useMemo(
    () => Array.from(timers.values()).some((timer) => !timer.isPaused),
    [timers],
  );
  const shouldTick = options?.enableTick !== false && hasRunningTimers;
  
  // Start 1-second ticker when there are active timers
  useEffect(() => {
    if (shouldTick) {
      tickRef.current = setInterval(() => setTick(t => t + 1), 1000);
    } else {
      if (tickRef.current) clearInterval(tickRef.current);
      tickRef.current = null;
    }
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [shouldTick]);
  
  // Handle incoming operations event
  const handleOperationsMessage = useCallback((data: unknown, _channel: string) => {
    const normalized = normalizeOperationsRealtimeMessage(data);

    // Handle appointment_status_changed events (published by workflow-state route)
    if (normalized?.event === 'appointment_status_changed') {
      const event = normalized as unknown as AppointmentStatusEvent;
      const apptId = event.appointment_id;
      
      if (!apptId) return;
      
      // Filter if we have a whitelist
      if (trackedAppointmentIds && !trackedAppointmentIds.has(apptId)) return;
      
      const newStatus = event.new_status;

      setStatusOverrides(prev => {
        if (prev.get(apptId) === newStatus) {
          return prev;
        }

        const next = new Map(prev);
        next.set(apptId, newStatus);
        return next;
      });
      wsStatusRef.current.set(apptId, newStatus);

      const currentUserSnapshot = getCurrentUserAssigneeSnapshot(event, userId);
      const currentUserTimerDirective = currentUserSnapshot
        ? getTimerDirectiveFromAssigneeSnapshot({
            appointmentId: apptId,
            event,
            snapshot: currentUserSnapshot,
          })
        : null;

      if (currentUserTimerDirective) {
        if ('clear' in currentUserTimerDirective) {
          setTimers(prev => {
            const next = new Map(prev);
            next.delete(apptId);
            return next;
          });
          persistTimerRemoval([apptId]);
          return;
        }

        const nextTimer = currentUserTimerDirective.timer;
        setTimers(prev => {
          const next = new Map(prev);
          next.set(apptId, nextTimer);
          return next;
        });
        persistTimerUpsert([nextTimer], 'websocket');
        return;
      }
      
      if (newStatus === 'on_route') {
        // Start en_route timer
        const nextTimer: AppointmentTimerState = {
          appointmentId: apptId,
          activeState: 'en_route',
          startedAt: event.travel_started_at || event.timestamp || new Date().toISOString(),
          scheduledStart: event.scheduled_start,
          scheduledEnd: event.scheduled_end,
          userId: event.user_id,
          userName: event.user_name,
          elapsedOffsetSeconds: 0,
          isPaused: false,
        };
        setTimers(prev => {
          const next = new Map(prev);
          next.set(apptId, nextTimer);
          return next;
        });
        persistTimerUpsert([nextTimer], 'websocket');
      } else if (newStatus === 'on_site') {
        // Arrived — stop travel/work counters and clear any cached timer state
        setTimers(prev => {
          const next = new Map(prev);
          next.delete(apptId);
          return next;
        });
        persistTimerRemoval([apptId]);
      } else if (newStatus === 'in_progress') {
        // Checked in — start in_progress timer
        let nextTimer: AppointmentTimerState | null = null;
        setTimers(prev => {
          const next = new Map(prev);
          const existing = next.get(apptId);
          const resumingPausedWork = Boolean(event.workflow_resumed_at)
            || (existing?.activeState === 'in_progress' && existing.isPaused);
          const storedElapsedOffsetSeconds = getNonNegativeFiniteSeconds(event.accumulated_work_seconds) ?? 0;
          nextTimer = {
            appointmentId: apptId,
            activeState: 'in_progress',
            startedAt: resumingPausedWork
              ? (event.workflow_resumed_at || event.timestamp || new Date().toISOString())
              : (event.check_in_timestamp || event.timestamp || new Date().toISOString()),
            scheduledStart: event.scheduled_start || existing?.scheduledStart,
            scheduledEnd: event.scheduled_end || existing?.scheduledEnd,
            userId: event.user_id || existing?.userId,
            userName: event.user_name || existing?.userName,
            elapsedOffsetSeconds: resumingPausedWork
              ? Math.max(storedElapsedOffsetSeconds, Math.max(0, existing?.elapsedOffsetSeconds || 0))
              : storedElapsedOffsetSeconds,
            isPaused: false,
          };
          next.set(apptId, nextTimer);
          return next;
        });
        if (nextTimer) {
          persistTimerUpsert([nextTimer], 'websocket');
        }
      } else if (newStatus === 'paused') {
        let nextTimer: AppointmentTimerState | null = null;
        setTimers(prev => {
          const next = new Map(prev);
          const existing = next.get(apptId);
          const pauseTimestamp = event.workflow_paused_at || event.timestamp;
          const storedElapsedOffsetSeconds = getNonNegativeFiniteSeconds(event.accumulated_work_seconds) ?? 0;
          nextTimer = {
            appointmentId: apptId,
            activeState: existing?.activeState === 'en_route' ? 'en_route' : 'in_progress',
            startedAt: pauseTimestamp || existing?.startedAt || new Date().toISOString(),
            scheduledStart: event.scheduled_start || existing?.scheduledStart,
            scheduledEnd: event.scheduled_end || existing?.scheduledEnd,
            userId: event.user_id || existing?.userId,
            userName: event.user_name || existing?.userName,
            elapsedOffsetSeconds: Math.max(
              storedElapsedOffsetSeconds,
              existing ? getElapsedOffsetAtTimestamp(existing, pauseTimestamp) : 0,
            ),
            isPaused: true,
          };
          next.set(apptId, nextTimer);
          return next;
        });
        if (nextTimer) {
          persistTimerUpsert([nextTimer], 'websocket');
        }
      } else if (shouldClearAppointmentTimerOnStatusEvent(event)) {
        // Terminal or completion outcome — remove timer for this subscriber (all assignees receive the same event)
        setTimers(prev => {
          const next = new Map(prev);
          next.delete(apptId);
          return next;
        });
        persistTimerRemoval([apptId]);
      } else {
        // Scheduled / confirmed / other inactive statuses should clear any cached timer state.
        setTimers(prev => {
          const next = new Map(prev);
          next.delete(apptId);
          return next;
        });
        persistTimerRemoval([apptId]);
      }
    }
  }, [persistTimerRemoval, persistTimerUpsert, trackedAppointmentIds, userId]);
  
  // Subscribe to operations channel
  const channel = clientId ? `operations:client:${clientId}` : undefined;
  useWebSocketChannel({
    channel,
    onMessage: handleOperationsMessage,
  });

  useWebSocketChannel({
    channel: realtimeTaskChannel,
    onMessage: handleOperationsMessage,
  });
  
  // Seed timers from existing appointment data (already active when component mounts)
  const seedFromAppointments = useCallback((appointments: Array<{
    id: string;
    status: string;
    scheduled_start?: string;
    scheduled_end?: string;
    custom_fields?: {
      travel_started_at?: string;
      check_in_timestamp?: string;
      arrived_at?: string;
      workflow_paused_at?: string;
      workflow_resumed_at?: string;
      work_started_at?: string;
      accumulated_work_seconds?: number | string;
      [key: string]: unknown;
    };
    assignees?: Array<{ user_id: number; first_name?: string; last_name?: string }>;
    assigned_to?: number | number[] | null;
  }>) => {
    const seededTimers: AppointmentTimerState[] = [];
    const removedTimerIds: string[] = [];
    setStatusOverrides(prev => {
      let changed = false;
      const next = new Map(prev);

      for (const appt of appointments) {
        // WebSocket is the SSOT for status — once it has established a status,
        // seed data must never overwrite it (prevents stale cache on re-seed).
        if (wsStatusRef.current.has(appt.id)) {
          continue;
        }

        if (next.get(appt.id) !== appt.status) {
          next.set(appt.id, appt.status);
          changed = true;
        }
      }

      return changed ? next : prev;
    });

    setTimers(prev => {
      const next = new Map(prev);
      let changed = false;
      
      for (const appt of appointments) {
        // WebSocket is the SSOT for status — once it has established a status,
        // seed data must never overwrite the timer (prevents stale cache on re-seed).
        if (wsStatusRef.current.has(appt.id)) {
          continue;
        }

        const cf = appt.custom_fields;
        let seededTimer: AppointmentTimerState | null = null;
        
        if (appt.status === 'on_route' && cf?.travel_started_at) {
          seededTimer = {
            appointmentId: appt.id,
            activeState: 'en_route',
            startedAt: cf.travel_started_at,
            scheduledStart: appt.scheduled_start,
            scheduledEnd: appt.scheduled_end,
            elapsedOffsetSeconds: 0,
            isPaused: false,
          } satisfies AppointmentTimerState;
        } else if (appt.status === 'in_progress' && cf?.check_in_timestamp) {
          const existing = next.get(appt.id);
          const storedWorkElapsedSeconds = getStoredWorkElapsedSeconds(cf);
          const pausedOffsetSeconds = existing?.isPaused ? Math.max(0, existing.elapsedOffsetSeconds || 0) : 0;
          const elapsedOffsetSeconds = Math.max(storedWorkElapsedSeconds, pausedOffsetSeconds);
          seededTimer = {
            appointmentId: appt.id,
            activeState: 'in_progress',
            startedAt: getWorkSegmentStartedAt(cf, {
              preferResumeSegment: elapsedOffsetSeconds > 0,
            }),
            scheduledStart: appt.scheduled_start,
            scheduledEnd: appt.scheduled_end,
            elapsedOffsetSeconds,
            isPaused: false,
          } satisfies AppointmentTimerState;
        } else if (appt.status === 'paused') {
          const existing = next.get(appt.id);
          const workflowPausedAt = typeof cf?.workflow_paused_at === 'string' ? cf.workflow_paused_at : undefined;
          const storedWorkElapsedSeconds = getStoredWorkElapsedSeconds(cf);
          const existingElapsedSeconds = existing
            ? getElapsedOffsetAtTimestamp(existing, workflowPausedAt)
            : 0;
          seededTimer = {
            appointmentId: appt.id,
            activeState: 'in_progress',
            startedAt: workflowPausedAt || getWorkSegmentStartedAt(cf, {
              preferResumeSegment: true,
              fallback: existing?.startedAt,
            }),
            scheduledStart: appt.scheduled_start,
            scheduledEnd: appt.scheduled_end,
            elapsedOffsetSeconds: Math.max(storedWorkElapsedSeconds, existingElapsedSeconds),
            isPaused: true,
          } satisfies AppointmentTimerState;
        }

        if (seededTimer) {
          const existing = next.get(appt.id);
          const timerChanged = (
            !existing
            || existing.activeState !== seededTimer.activeState
            || existing.startedAt !== seededTimer.startedAt
            || existing.scheduledStart !== seededTimer.scheduledStart
            || existing.scheduledEnd !== seededTimer.scheduledEnd
            || existing.userId !== seededTimer.userId
            || existing.userName !== seededTimer.userName
            || (existing.elapsedOffsetSeconds ?? 0) !== (seededTimer.elapsedOffsetSeconds ?? 0)
            || (existing.isPaused ?? false) !== (seededTimer.isPaused ?? false)
          );

          if (timerChanged) {
            next.set(appt.id, seededTimer);
            seededTimers.push(seededTimer);
            changed = true;
          }
          continue;
        }

        if (next.delete(appt.id)) {
          removedTimerIds.push(appt.id);
          changed = true;
        }
      }

      if (!changed) {
        return prev;
      }
      
      return next;
    });
    if (seededTimers.length > 0) {
      persistTimerUpsert(seededTimers, 'seed');
    }
    if (removedTimerIds.length > 0) {
      persistTimerRemoval(removedTimerIds);
    }
  }, [persistTimerRemoval, persistTimerUpsert]);
  
  // Remove a timer manually (e.g., when appointment is completed via UI)
  const removeTimer = useCallback((appointmentId: string) => {
    setTimers(prev => {
      const next = new Map(prev);
      next.delete(appointmentId);
      return next;
    });
    persistTimerRemoval([appointmentId]);
  }, [persistTimerRemoval]);
  
  // Compute display data for all timers (re-computed on every tick)
  const timerDisplays: Map<string, AppointmentTimerDisplay> = useMemo(() => {
    const now = Date.now();
    const displays = new Map<string, AppointmentTimerDisplay>();
    
    timers.forEach((timer, id) => {
      const isRunning = !timer.isPaused;
      const elapsedSeconds = getElapsedOffsetAtTimestamp(timer);
      
      const scheduledStartMs = timer.scheduledStart ? new Date(timer.scheduledStart).getTime() : 0;
      const scheduledEndMs = timer.scheduledEnd ? new Date(timer.scheduledEnd).getTime() : 0;
      
      // Late: en_route state and current time is past the appointment's scheduledStart
      const isLate = isRunning && timer.activeState === 'en_route' && scheduledStartMs > 0 && now > scheduledStartMs;
      const lateSeconds = isLate ? Math.floor((now - scheduledStartMs) / 1000) : 0;
      
      // Over schedule: in_progress state and current time is past scheduledEnd
      const isOverSchedule = isRunning && timer.activeState === 'in_progress'
        && scheduledEndMs > 0 && now > scheduledEndMs;
      
      displays.set(id, {
        ...timer,
        elapsedSeconds,
        elapsedFormatted: formatElapsed(elapsedSeconds),
        isRunning,
        isLate,
        isOverSchedule,
        lateSeconds,
      });
    });
    
    return displays;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timers, tick]);
  
  // Get timer for a specific appointment
  const getTimer = useCallback((appointmentId: string): AppointmentTimerDisplay | null => {
    return timerDisplays.get(appointmentId) || null;
  }, [timerDisplays]);

  const getStatus = useCallback((appointmentId: string): string | null => {
    return statusOverrides.get(appointmentId) || null;
  }, [statusOverrides]);
  
  return {
    timers: timerDisplays,
    getTimer,
    getStatus,
    statuses: statusOverrides,
    seedFromAppointments,
    removeTimer,
    hasActiveTimers,
  };
}

export default useAppointmentLiveTimers;
